import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  RefreshRounded,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  type FormEvent,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getProperties, type PropertyRecord } from "../api/properties";
import { getRooms, type RoomRecord } from "../api/rooms";
import {
  createMaintenanceIssue,
  deleteMaintenanceIssue,
  getMaintenanceIssues,
  updateMaintenanceIssue,
  type MaintenanceIssueRecord,
} from "../api/maintenance";
import { useNotifications } from "../components/NotificationProvider";

type MaintenanceFormState = {
  property_id: string;
  room_id: string;
  title: string;
  description: string;
  priority: string;
  status: string;
};

type MaintenanceModalMode = "create" | "edit";

const createInitialFormState = (): MaintenanceFormState => ({
  property_id: "",
  room_id: "",
  title: "",
  description: "",
  priority: "medium",
  status: "open",
});

export function MaintenancePage() {
  const [issues, setIssues] = useState<MaintenanceIssueRecord[]>([]);
  const [properties, setProperties] = useState<PropertyRecord[]>([]);
  const [rooms, setRooms] = useState<RoomRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<MaintenanceModalMode>("create");
  const [selectedIssue, setSelectedIssue] =
    useState<MaintenanceIssueRecord | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [issueToDelete, setIssueToDelete] =
    useState<MaintenanceIssueRecord | null>(null);
  const [formState, setFormState] = useState<MaintenanceFormState>(
    createInitialFormState,
  );
  const { showAlert, showSnackbar } = useNotifications();

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [issueData, propertyData, roomData] = await Promise.all([
        getMaintenanceIssues(),
        getProperties(),
        getRooms(),
      ]);
      setIssues(issueData);
      setProperties(propertyData);
      setRooms(roomData);
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Unable to load maintenance issues",
        message: "Please refresh and try again.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const filteredIssues = useMemo(() => {
    const term = deferredSearchTerm.trim().toLowerCase();
    if (!term) return issues;

    return issues.filter((issue) => {
      const room = rooms.find((item) => item.id === issue.room_id);
      const propertyName =
        properties.find((item) => item.id === room?.property_id)
          ?.property_name ?? "";
      const haystack = [
        issue.title,
        issue.description ?? "",
        issue.priority,
        issue.status,
        propertyName,
        room?.room_number ?? "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [deferredSearchTerm, issues, properties, rooms]);

  const openCreateModal = () => {
    setModalMode("create");
    setSelectedIssue(null);
    setFormState({
      ...createInitialFormState(),
      property_id: properties[0]?.id?.toString() ?? "",
      room_id: rooms[0]?.id?.toString() ?? "",
    });
    setIsModalOpen(true);
  };

  const openEditModal = (issue: MaintenanceIssueRecord) => {
    setModalMode("edit");
    setSelectedIssue(issue);
    setFormState({
      property_id: issue.property_id?.toString() ?? "",
      room_id: issue.room_id?.toString() ?? "",
      title: issue.title,
      description: issue.description ?? "",
      priority: issue.priority,
      status: issue.status,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedIssue(null);
    setFormState(createInitialFormState());
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formState.title.trim()) {
      showAlert({
        title: "Missing details",
        message: "A title is required.",
        variant: "error",
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        property_id: formState.property_id
          ? Number(formState.property_id)
          : null,
        room_id: formState.room_id ? Number(formState.room_id) : null,
        title: formState.title.trim(),
        description: formState.description.trim() || null,
        priority: formState.priority,
        status: formState.status,
      };

      if (modalMode === "edit" && selectedIssue) {
        await updateMaintenanceIssue(selectedIssue.id, payload);
        showSnackbar({
          title: "Issue updated",
          message: "The maintenance issue was updated successfully.",
          variant: "success",
        });
      } else {
        await createMaintenanceIssue(payload);
        showSnackbar({
          title: "Issue logged",
          message: "The maintenance issue is now visible.",
          variant: "success",
        });
      }

      closeModal();
      await loadData();
    } catch (error) {
      console.error(error);
      showAlert({
        title:
          modalMode === "edit"
            ? "Unable to update issue"
            : "Unable to log issue",
        message: "Please check the details and try again.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteConfirmation = (issue: MaintenanceIssueRecord) => {
    setIssueToDelete(issue);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!issueToDelete) return;
    setDeleteDialogOpen(false);
    setIsLoading(true);
    try {
      await deleteMaintenanceIssue(issueToDelete.id);
      await loadData();
      showSnackbar({
        title: "Issue deleted",
        message: "The maintenance issue was removed.",
        variant: "info",
      });
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Unable to delete issue",
        message: "Please try again shortly.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
      setIssueToDelete(null);
    }
  };

  return (
    <Stack spacing={3} sx={{ width: "100%", position: "relative" }}>
      {isLoading && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: -8,
            left: 0,
            right: 0,
            borderRadius: 6,
          }}
        />
      )}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 6,
          p: { xs: 2, md: 3 },
          bgcolor: "background.paper",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by title, priority, or location..."
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              maxHeight: 40,
              "& .MuiOutlinedInput-root": { borderRadius: 6 },
            }}
          />
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh issues">
              <IconButton
                onClick={() => void loadData()}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 6,
                }}
              >
                <RefreshRounded fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateModal}
              disableElevation
              sx={{ borderRadius: 6, px: 3, whiteSpace: "nowrap", height: 40 }}
            >
              Log issue
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 6, p: { xs: 2, md: 3 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Maintenance issues ({filteredIssues.length})
        </Typography>

        <Stack spacing={2}>
          {filteredIssues.map((issue) => {
            const room = rooms.find((item) => item.id === issue.room_id);
            const propertyName =
              properties.find((item) => item.id === room?.property_id)
                ?.property_name ?? "Unknown property";
            return (
              <Card
                key={issue.id}
                variant="outlined"
                sx={{
                  borderRadius: 6,
                  transition: "box-shadow 0.2s ease, transform 0.2s ease",
                  "&:hover": { boxShadow: 2, borderColor: "primary.main" },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                    "&:last-child": { pb: 2 },
                  }}
                >
                  <Box sx={{ minWidth: 240 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {issue.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {propertyName} · Room{" "}
                      {room?.room_number ?? "Not assigned"}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mt: 1.25, flexWrap: "wrap" }}
                    >
                      <Chip
                        label={issue.priority}
                        size="small"
                        color={
                          issue.priority === "critical"
                            ? "error"
                            : issue.priority === "high"
                              ? "warning"
                              : "default"
                        }
                        sx={{
                          borderRadius: 6,
                          fontWeight: 500,
                          textTransform: "capitalize",
                        }}
                      />
                      <Chip
                        label={issue.status}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{
                          borderRadius: 6,
                          fontWeight: 500,
                          textTransform: "capitalize",
                        }}
                      />
                    </Stack>
                  </Box>

                  <Box sx={{ minWidth: 260 }}>
                    <Typography variant="body2" color="text.secondary">
                      {issue.description ?? "No description provided"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      Reported:{" "}
                      {issue.reported_at
                        ? new Date(issue.reported_at).toLocaleDateString()
                        : "Unknown"}
                    </Typography>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignSelf={{ xs: "flex-end", sm: "center" }}
                  >
                    <Tooltip title="Edit issue">
                      <IconButton
                        color="primary"
                        onClick={() => openEditModal(issue)}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 6,
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete issue">
                      <IconButton
                        color="error"
                        onClick={() => openDeleteConfirmation(issue)}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 6,
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>

        {filteredIssues.length === 0 && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              No maintenance issues match your current search.
            </Typography>
          </Box>
        )}
      </Paper>

      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        PaperProps={{ sx: { borderRadius: 6, width: "100%", maxWidth: 700 } }}
      >
        <DialogTitle>
          {modalMode === "edit" ? "Edit issue" : "Log maintenance issue"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Record property or room maintenance work with clear priority and
            status tracking.
          </DialogContentText>
          <Box
            component="form"
            id="maintenance-form"
            onSubmit={handleSubmit}
            noValidate
          >
            <Stack spacing={2.25}>
              <TextField
                value={formState.title}
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    title: event.target.value,
                  }))
                }
                label="Title"
                required
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />
              <TextField
                value={formState.description}
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
                label="Description"
                multiline
                minRows={3}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <FormControl fullWidth>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Property
                  </Typography>
                  <Select
                    value={formState.property_id}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        property_id: event.target.value,
                        room_id: "",
                      }))
                    }
                    sx={{ borderRadius: 6 }}
                  >
                    {properties.map((property) => (
                      <MenuItem key={property.id} value={property.id}>
                        {property.property_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Room
                  </Typography>
                  <Select
                    value={formState.room_id}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        room_id: event.target.value,
                      }))
                    }
                    sx={{ borderRadius: 6 }}
                  >
                    {rooms
                      .filter(
                        (room) =>
                          !formState.property_id ||
                          room.property_id === Number(formState.property_id),
                      )
                      .map((room) => (
                        <MenuItem key={room.id} value={room.id}>
                          Room {room.room_number}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <FormControl fullWidth>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Priority
                  </Typography>
                  <Select
                    value={formState.priority}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        priority: event.target.value,
                      }))
                    }
                    sx={{ borderRadius: 6 }}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Status
                  </Typography>
                  <Select
                    value={formState.status}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        status: event.target.value,
                      }))
                    }
                    sx={{ borderRadius: 6 }}
                  >
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="in_progress">In progress</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeModal} color="inherit" sx={{ borderRadius: 6 }}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="maintenance-form"
            variant="contained"
            disableElevation
            sx={{ borderRadius: 6 }}
          >
            {modalMode === "edit" ? "Update issue" : "Save issue"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 6 } }}
      >
        <DialogTitle>Delete issue</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this maintenance issue? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="inherit"
            sx={{ borderRadius: 6 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disableElevation
            sx={{ borderRadius: 6 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
